import { useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { datePattern, formatPattern, timePattern } from '../lib/format'

/**
 * Live date/time pill for the Super Admin header.
 *
 * Renders a "live" green dot + the current time in the person's zone and
 * formats (default `DD/MM/YYYY, hh:mm:ss AM/PM`), ticking once a second. A refresh
 * button re-syncs to `new Date()` immediately and spins briefly as
 * visual feedback (the clock already self-updates, so the button is
 * an affordance + manual prompt rather than a data fetch).
 *
 * Styling uses the shared header tokens (`--color-card-hover`,
 * `--color-rule`, `--color-ink`) so it sits flush with FontSizeMenu /
 * NotificationsBell / UserMenu to its right. Tabular numerals keep the
 * width stable so the surrounding controls don't shift as digits change.
 */
function formatNow(d) {
  // The person's zone, date format and 12/24-hour clock; seconds kept so
  // the pill visibly ticks. Default: "DD/MM/YYYY, hh:mm:ss AM/PM".
  return formatPattern(d, `${datePattern()}, ${timePattern().replace('mm', 'mm:ss')}`)
}

export function DateTimeClock() {
  const [now, setNow] = useState(() => new Date())
  const [spinning, setSpinning] = useState(false)

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    // Browsers throttle (or pause) setInterval in background tabs and on
    // sleep/wake, so the clock can sit stale until the next throttled
    // tick fires — which reads as "not updating in real-time" when you
    // switch back to the dashboard. Re-sync immediately whenever the tab
    // becomes visible or regains focus so the displayed time is always
    // current the moment the user looks at it.
    const resync = () => {
      if (!document.hidden) setNow(new Date())
    }
    document.addEventListener('visibilitychange', resync)
    window.addEventListener('focus', resync)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', resync)
      window.removeEventListener('focus', resync)
    }
  }, [])

  function handleRefresh() {
    setNow(new Date())
    setSpinning(true)
    // Clear the spin class after one rotation so a re-click re-triggers it.
    setTimeout(() => setSpinning(false), 500)
  }

  return (
    <div
      className="inline-flex items-center gap-2.5 h-[34px] pl-3 pr-2 rounded-full bg-[color:var(--color-card-hover)] border border-[color:var(--color-rule)] hover:border-[color:var(--color-rule-strong)] transition-colors"
      title="Current platform time"
    >
      {/* Live indicator — pulsing emerald dot. */}
      <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full rounded-full bg-[color:var(--color-success)] opacity-40 animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[color:var(--color-success)]" />
      </span>

      <span className="text-[13px] font-semibold tabular-nums whitespace-nowrap text-[color:var(--color-ink)]">
        {formatNow(now)}
      </span>

      <button
        type="button"
        onClick={handleRefresh}
        title="Refresh"
        aria-label="Refresh time"
        className="inline-flex items-center justify-center h-[22px] w-[22px] rounded-md text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-accent)] hover:bg-[color:var(--color-accent-bg)] cursor-pointer transition-colors"
      >
        <RefreshCw size={14} className={spinning ? 'animate-spin' : ''} />
      </button>
    </div>
  )
}
